import React, { useCallback } from 'react';
import { reorder } from '@alltrails/shared/utils/listHelpers';
import { func, bool, number, arrayOf, object } from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { waypointPropTypes } from '../../../proptypes/shared';
import WaypointCard from '../WaypointCard';
import WaypointForm from '../WaypointForm';
import { UPDATE_EDIT_INDEX, REMOVE_WAYPOINT, TOGGLE_SORT_MODE, REORDER_WAYPOINTS, TOGGLE_SHOW_TRANLSATED_LANG } from '../../../hooks/useWaypoints';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { reorderWaypoints, deleteWaypoint } from '../../../utils/requests/at_map_requests';

const Waypoints = ({
  waypoints,
  editIndex,
  dispatchWaypoints,
  messagingChannel,
  mapId,
  sortMode,
  canEdit,
  trailId,
  isAdmin,
  createMapWithWaypoint,
  dispatchFormState
}) => {
  let editWaypoint;
  let handleDeleteWaypoint;
  let persistWaypointOrder;
  let toggleSortMode;
  let onDragEnd;

  if (canEdit) {
    editWaypoint = payload => {
      if (!canEdit) {
        return;
      }

      dispatchWaypoints({ type: UPDATE_EDIT_INDEX, payload });
    };

    handleDeleteWaypoint = id => {
      if (!canEdit || !confirm(StringConstantsMixin.strings.TRACK_DETAIL.DELETE_WP_QUESTION)) {
        return;
      }

      deleteWaypoint(mapId, id).then(() => {
        dispatchWaypoints({ type: REMOVE_WAYPOINT, payload: id });
      });
    };

    persistWaypointOrder = () => {
      const orderedWaypointIds = waypoints.map(wp => wp.id);
      reorderWaypoints(mapId, orderedWaypointIds).then(() => {
        dispatchWaypoints({ type: TOGGLE_SORT_MODE, payload: false });
      });
    };

    toggleSortMode = useCallback(() => {
      dispatchWaypoints({ type: TOGGLE_SORT_MODE, payload: !sortMode });
    }, [sortMode]);

    onDragEnd = result => {
      const payload = reorder(waypoints, result.source.index, result.destination.index).map((wp, idx) => {
        return { ...wp, order: idx + 1 };
      });

      dispatchWaypoints({ type: REORDER_WAYPOINTS, payload });
    };
  }

  const handleMouseOver = (id, location) => {
    if (!messagingChannel) {
      return;
    }

    messagingChannel.publish({
      topic: 'waypoint.hover',
      data: { id, location }
    });
  };

  if (sortMode) {
    const renderDraggableWaypoint = (waypoint, idx) => {
      return (
        <Draggable key={`${waypoint.id}`} draggableId={`${waypoint.id}`} index={idx}>
          {provided => (
            <li ref={provided.innerRef} {...provided.draggableProps}>
              <WaypointCard
                {...waypoint}
                provided={provided}
                sortMode={sortMode}
                canEdit={canEdit}
                key={waypoint.id}
                idx={idx}
                toggleSortMode={toggleSortMode}
                persistWaypointOrder={persistWaypointOrder}
              />
            </li>
          )}
        </Draggable>
      );
    };

    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {provided => (
            <ul {...provided.droppableProps} ref={provided.innerRef}>
              {waypoints.map((waypoint, idx) => {
                return renderDraggableWaypoint(waypoint, idx);
              })}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    );
  }

  const toggleLanguageOrigin = (idx, showInTranslatedLang) => {
    dispatchWaypoints({ type: TOGGLE_SHOW_TRANLSATED_LANG, payload: { idx, showInTranslatedLang: !showInTranslatedLang } });
  };

  return waypoints.map((waypoint, idx) => {
    return idx === editIndex && canEdit ? (
      <WaypointForm
        key="waypoint-form"
        {...waypoint}
        dispatchWaypoints={dispatchWaypoints}
        messagingChannel={messagingChannel}
        mapId={mapId}
        trailId={trailId}
        isAdmin={isAdmin}
        createMapWithWaypoint={createMapWithWaypoint}
        dispatchFormState={dispatchFormState}
      />
    ) : (
      <WaypointCard
        key={waypoint.id}
        {...waypoint}
        idx={idx}
        editWaypoint={editWaypoint}
        deleteWaypoint={handleDeleteWaypoint}
        totalWaypointsLength={waypoints.length}
        toggleSortMode={toggleSortMode}
        sortMode={sortMode}
        canEdit={canEdit}
        isAdmin={isAdmin}
        handleMouseOver={handleMouseOver}
        toggleLanguageOrigin={toggleLanguageOrigin}
      />
    );
  });
};

Waypoints.propTypes = {
  waypoints: arrayOf(waypointPropTypes),
  dispatchWaypoints: func,
  editIndex: number,
  messagingChannel: object,
  mapId: number,
  sortMode: bool,
  canEdit: bool,
  trailId: number,
  isAdmin: bool,
  dispatchFormState: bool
};

Waypoints.defaultProps = {
  trailId: null,
  waypoints: [],
  editIndex: null,
  messagingChannel: null,
  mapId: null,
  sortMode: false,
  canEdit: false,
  isAdmin: false,
  dispatchFormState: false
};

export default Waypoints;
